// src/weather/wxEvidence.js — the rules behind the Weather Records tool.
//
// Kevin, 2026-10-13: *"a weather tool that we can use for any insurance situation that
// could be weather related: Prevent Plant supporting documents … Freeze data … Drought
// … including FSA SDRP like programs where we need supporting documents for payments."*
//
// Pure: no imports, no fetch, no React. Everything here is arithmetic over rows the
// caller supplies, so `tools/check-wx-evidence.js` can drive the SHIPPED rules offline
// and the API and the page cannot disagree about what a number means.
//
// ⚠ EVERY FIGURE CARRIES ITS RESOLUTION AND ITS SOURCE. This output is meant to be
// printed and handed to an adjuster or to FSA, and the three things that make it
// evidence rather than a number are: WHERE it was measured, HOW FAR that is from the
// ground in question, and WHICH instrument said so. A reading with none of those is a
// decoration. Nothing here is allowed to return a bare value.

// ── UNITS ──────────────────────────────────────────────────────────────────────
// Stored in metric (gridMET is metric at source); every reader here is American.
// Converted once, in one place, so a document cannot mix them.
// ⚠ A MISSING READING IS NOT ZERO, AND `Number(null)` IS. This repo already records
// that exact failure on the weather sync — unknown rainfall written as 0 reads
// downstream as a drought that is not happening — and it came back here through
// `Number(r.precip_mm)`, which turns null, undefined and "" all into 0. On this page a
// day the instrument did not report would have printed 0.00", counted as a dry day, and
// extended a "longest dry run" on a document somebody files. `num()` is the only way a
// stored value becomes a number in this module.
const num = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const mmToIn = (mm) => (mm == null ? null : mm / 25.4);
const cToF = (c) => (c == null ? null : (c * 9) / 5 + 32);
const r2 = (n) => (n == null ? null : Math.round(n * 100) / 100);
const r1 = (n) => (n == null ? null : Math.round(n * 10) / 10);

// ── THE LATTICE, STATED CORRECTLY ──────────────────────────────────────────────
// ⚠ THE CELLS ARE NOT 4 km APART. gridMET's SOURCE grid is 4 km, and parts of this
// repo say "4km grid cell" for that reason — but `wny_grid_cells` samples it on a
// 0.1° lattice. Measured 2026-10-13 over all 2,621 cells: 53 distinct latitudes and
// 85 longitudes, modal step 0.1°, which at 43°N is 11.1 km north-south and 8.1 km
// east-west. So the nearest sample to a clicked point can be ~6 km away INSIDE the
// footprint, and a document that calls this "4 km data" overstates its precision.
const LATTICE_DEG = 0.1;
const LATTICE_KM_NS = 11.1;
const LATTICE_KM_EW = 8.1;

// How far a snap may be before it stops being "here". Half a cell diagonal is ~6.9 km;
// beyond that the sample is in the NEXT cell over and the reader should see that said.
const SNAP_NEAR_KM = 7;
// ⚠ AND THE DATABASE WILL SNAP ACROSS A STATE LINE. `wny_cell_for()` accepts anything
// inside ~20 miles, so a Harford County MARYLAND point returns a York County
// PENNSYLVANIA cell 29.2 km away and looks like an ordinary answer (measured
// 2026-10-13). On a claims document that is a different place. Past this it is still
// returned — refusing would hide the only reading available — but it is marked FAR and
// the county it actually came from is printed beside it.
const SNAP_FAR_KM = 20;

function haversineKm(aLat, aLon, bLat, bLon) {
  if ([aLat, aLon, bLat, bLon].some((v) => !Number.isFinite(Number(v)))) return null;
  const R = 6371, rad = (x) => (Number(x) * Math.PI) / 180;
  const dLat = rad(bLat - aLat), dLon = rad(bLon - aLon);
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// What the page must show about WHERE a reading came from. Never optional.
function describeSnap(point, cell) {
  if (!cell) {
    return {
      ok: false,
      reason: "No weather grid covers this point. The grid is New York and Pennsylvania only "
            + "(2,621 cells); there is no stored weather for Maryland, Ohio, Indiana or Michigan "
            + "at any resolution.",
    };
  }
  const km = haversineKm(point.lat, point.lon, cell.lat, cell.lon);
  const miles = km == null ? null : km / 1.609344;
  const far = km != null && km > SNAP_NEAR_KM;
  return {
    ok: true,
    cell_id: cell.id,
    cell_lat: cell.lat, cell_lon: cell.lon,
    county: cell.county_name || null,
    state_fips: cell.state_fips || null,
    distance_km: r2(km), distance_mi: r2(miles),
    far,
    // ⚠ The sentence, not just the flag — whoever prints this must not have to
    // reconstruct the caveat from a boolean.
    note: km == null ? null
      : far
        ? `Nearest grid sample is ${r1(miles)} mi away, in ${cell.county_name || "another county"}. `
          + `The grid is sampled every ${LATTICE_DEG}° (about ${LATTICE_KM_NS} km north-south, `
          + `${LATTICE_KM_EW} km east-west), so this reading describes that sample, not the point clicked.`
        : `Nearest grid sample is ${r1(miles)} mi away, in ${cell.county_name || "this county"}.`,
    lattice: { deg: LATTICE_DEG, km_ns: LATTICE_KM_NS, km_ew: LATTICE_KM_EW },
  };
}

// ── THE DAILY SERIES ───────────────────────────────────────────────────────────
// `rows`   : wny_grid_weather_daily rows for one cell, any window
// `norms`  : wny_grid_climatology rows for that cell, keyed by day_of_year
// `fieldRows`: field_precip_daily rows for one CLU, when the caller resolved one
//
// ⚠ TWO RAINFALL SOURCES LIVE IN ONE COLUMN. `precip_mm` is MRMS where MRMS ran and
// gridMET otherwise, and the row's own `source` says which ("mrms1km" / "gridmet").
// They disagree day to day — 8.5 vs 9.3 mm on one cell, measured — because one is
// gauge-corrected radar and the other is modelled reanalysis. A document that does not
// name the instrument per day is not evidence, so the source rides on every row.
const SOURCE_LABEL = {
  mrms1km: "MRMS (gauge-corrected radar, 1 km)",
  gridmet: "gridMET (modelled reanalysis, 4 km source)",
  field: "MRMS 1 km, binned to this field's CLU boundary",
};

// ── ONE SOURCE PER NUMBER ──────────────────────────────────────────────────────
// `wny_grid_weather_daily.precip_mm` flips between MRMS radar and gridMET day to day
// (the row's `source` says which), while `wny_grid_climatology` is built from gridMET
// alone. Summing the flipping column and dividing by the gridMET normal mixes two
// instruments: Livingston 2026 read 105% of normal that way and 100% on gridMET alone,
// because radar ran 2–9% above gridMET across Genesee, Livingston and Wyoming.
// Anything compared to the normal reads THIS — the gridMET value, from its own column
// when stored, else `precip_mm` only when the row says it IS gridMET. No gridMET for
// the day (it lags ~2 days, and a few days are missing) is null: a gap, never zero.
// Forecast rows are never observations.
function gridmetPrecipMm(r) {
  if (!r || r.is_forecast === true) return null;
  const g = r.precip_gridmet_mm;
  if (g != null && g !== "" && Number.isFinite(Number(g))) return Number(g);
  if (r.source === "gridmet" && r.precip_mm != null && r.precip_mm !== "" && Number.isFinite(Number(r.precip_mm))) return Number(r.precip_mm);
  return null;
}
// The radar reading for the day, for when a screen wants the newest days gridMET has
// not published yet. Labelled radar wherever it is shown, and never set against the
// gridMET normal.
function radarPrecipMm(r) {
  if (!r || r.is_forecast === true) return null;
  const m = r.precip_mrms_mm;
  if (m != null && m !== "" && Number.isFinite(Number(m))) return Number(m);
  if (r.source === "mrms1km" && r.precip_mm != null && r.precip_mm !== "" && Number.isFinite(Number(r.precip_mm))) return Number(r.precip_mm);
  return null;
}

function dayOfYear(iso) {
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return null;
  const start = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.floor((d.getTime() - start) / 86400000) + 1;
}

function buildSeries({ rows = [], norms = [], fieldRows = [], gddBase = 50, freezeF = 28 } = {}) {
  const normBy = new Map();
  for (const n of norms) {
    const k = Number(n.day_of_year);
    if (Number.isFinite(k)) normBy.set(k, n);
  }
  const fieldBy = new Map();
  for (const f of fieldRows) fieldBy.set(String(f.obs_date).slice(0, 10), f);

  const out = [];
  for (const r of rows) {
    const date = String(r.obs_date || "").slice(0, 10);
    if (!date) continue;
    const doy = dayOfYear(date);
    const n = doy == null ? null : normBy.get(doy) || null;

    const tmaxF = cToF(num(r.tmax_c));
    const tminF = cToF(num(r.tmin_c));
    // GDD, capped the way RMA/agronomy does: the day's max is held at 86F and the min
    // floored at the base before averaging. Stated, because an uncapped GDD is a
    // different number and nothing on a printed sheet says which convention it used.
    const hi = tmaxF == null ? null : Math.min(tmaxF, 86);
    const lo = tminF == null ? null : Math.max(tminF, gddBase);
    const gdd = hi == null || lo == null ? null : Math.max(0, (hi + lo) / 2 - gddBase);

    const fieldRow = fieldBy.get(date) || null;
    const gridIn = mmToIn(num(r.precip_mm));
    const fieldIn = fieldRow ? mmToIn(num(fieldRow.precip_mm)) : null;

    out.push({
      date,
      // Rainfall — the field-exact figure when we have one, with the grid beside it so
      // the two are never silently swapped.
      precip_in: r2(fieldIn != null ? fieldIn : gridIn),
      precip_grid_in: r2(gridIn),
      precip_field_in: r2(fieldIn),
      precip_source: fieldIn != null ? "field" : (r.source || null),
      precip_source_label: SOURCE_LABEL[fieldIn != null ? "field" : (r.source || "")] || (r.source || null),
      precip_gridmet_in: r2(mmToIn(num(r.precip_gridmet_mm))),
      precip_mrms_in: r2(mmToIn(num(r.precip_mrms_mm))),
      // ⚠ THE FIGURE THAT IS SET AGAINST THE NORMAL. The normal is gridMET 1995–2025, so
      // only the gridMET reading for the day can be divided by it. `precip_in` above is
      // what was measured (field radar first), and stays the headline; this is what
      // "% of normal" and "departure" are computed from. Null on a day gridMET has not
      // published, which then sits out of the comparison rather than counting as dry.
      precip_gm_in: r2(mmToIn(gridmetPrecipMm(r))),

      tmax_f: r1(tmaxF), tmin_f: r1(tminF),
      gdd: r1(gdd),
      srad_wm2: r1(num(r.srad_wm2)),
      vpd_kpa: r2(num(r.vpd_kpa)),

      // Normals, and the sample behind them. A departure quoted against a normal with
      // no stated year count is not checkable — same floor the MI briefs' pctileOf uses.
      normal_precip_in: n ? r2(mmToIn(num(n.precip_mm))) : null,
      normal_tmax_f: n ? r1(cToF(num(n.tmax_c))) : null,
      normal_tmin_f: n ? r1(cToF(num(n.tmin_c))) : null,
      // ⚠ THESE THREE ARE PUBLISHED AT FIXED SETTINGS AND THE PAGE'S ARE NOT.
      // `wny_grid_climatology` is built by tools/grid/build-weather.js at a HARDCODED
      // modified base 50F (86F cap), tmin <= 28F for a freeze day and tmax > 90F for a
      // heat day. The page lets the reader move the GDD base and the freeze threshold,
      // so these normals are only comparable at the settings they were computed at —
      // `compareToNormal` is where that is enforced, and it REFUSES rather than
      // quietly differencing two different quantities.
      normal_gdd50: n ? r1(num(n.gdd_f)) : null,
      normal_freeze28_prob: n ? num(n.freeze_day_prob) : null,
      normal_heat90_prob: n ? num(n.heat_day_prob) : null,
      normal_srad_wm2: n ? r1(num(n.srad_wm2)) : null,
      normal_vpd_kpa: n ? r2(num(n.vpd_kpa)) : null,
      normal_years: n ? num(n.year_count) : null,

      // Counted here so the page's own tally and the normal's definition cannot drift:
      // the heat-day normal is tmax > 90F, which is NOT the ">= 86F" display filter.
      heat90: tmaxF == null ? null : tmaxF > 90,

      freeze: tminF == null ? null : tminF <= freezeF,
      // A forecast row is NOT an observation and must never be printed as one.
      is_forecast: r.is_forecast === true,
    });
  }
  out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  // Running total, so a PP window reads as an accumulation rather than 60 separate days.
  // ⚠ OBSERVED ONLY, for the same reason the headline total is: a forecast day adding
  // to the cumulative would make the last row of the table exceed the total printed
  // above it, and the reader has no way to tell which one to believe.
  // ⚠ THE NORMAL HAS TO ACCUMULATE TOO, OR THE COLUMN BESIDE IT MEANS NOTHING.
  // Kevin, 2026-10-13, reading the table: *"normal here is also confusing because its
  // not cumulative, what is normal rainfall for selected range?"* The daily normal is
  // 0.06–0.20" while the cumulative beside it is 4.95", so the eye compares a season
  // against a day and the answer is noise. Both columns are running totals now, and
  // the last row of the normal column IS the answer to his question.
  //
  // ⚠ AND IT ACCUMULATES OVER THE SAME DAYS THE ACTUAL DOES. A normal summed over days
  // the instrument never reported is a longer period than the one being measured, and
  // the window would read drier than it was — the mixed-ratio class this repo already
  // records. A day with no observation advances NEITHER running total.
  //
  // ⚠ THE NORMAL RUNS BESIDE THE gridMET TOTAL, NOT THE MEASURED ONE. The normal is
  // gridMET, so its running total pairs with gridMET's: a radar day advanced the
  // measured column against a gridMET normal and Livingston read 105% where gridMET
  // alone read 100%. Three columns: what was measured, gridMET, and gridMET's normal
  // over the same gridMET days.
  let run = 0, runGm = 0, runNorm = 0;
  for (const d of out) {
    if (!d.is_forecast && d.precip_in != null) run += d.precip_in;
    const pairs = !d.is_forecast && d.precip_gm_in != null && d.normal_precip_in != null;
    if (pairs) { runGm += d.precip_gm_in; runNorm += d.normal_precip_in; }
    d.precip_cum_in = r2(run);
    d.gm_cum_in = r2(runGm);
    d.normal_cum_in = r2(runNorm);
  }
  return out;
}

// ── WHAT THE WINDOW ADDS UP TO ─────────────────────────────────────────────────
// ⚠ OBSERVATIONS AND FORECAST ARE SUMMED SEPARATELY. A PP or SDRP document that
// folded tomorrow's forecast into "rain that fell" would be stating a prediction as a
// fact. The forecast is reported, labelled, and kept out of the observed totals.
function summarize(series, { freezeF = 28, wetDayIn = 0.1, dryRunMin = 7 } = {}) {
  const obs = series.filter((d) => !d.is_forecast);
  const fc = series.filter((d) => d.is_forecast);
  const num = (xs) => xs.filter((v) => v != null);
  const sum = (xs) => num(xs).reduce((a, b) => a + b, 0);

  const precip = sum(obs.map((d) => d.precip_in));
  // ⚠ THE NORMAL SUMS OVER THE DAYS THE ACTUAL WAS MEASURED ON, NOT OVER EVERY DAY A
  // NORMAL EXISTS FOR. Those are different periods whenever the instrument missed a day,
  // and the difference lands straight in "% of normal" — which this page then printed
  // TWICE at two values: the summary card read 69.3% (3.28" over a 42-day normal) while
  // the strip immediately below it read 70.8% (the same 3.28" over the 41 days both
  // halves actually cover). Found by printing the sheet and reading it, not by a test.
  // Two differently-defined percentages of the same thing on one document is the Grain
  // Summary defect this repo already records; `compareToNormal`'s rule is the correct
  // one, so the card adopts it rather than the other way round.
  //
  // ⚠ AND THE ACTUAL IN THAT RATIO IS gridMET, BECAUSE THE NORMAL IS. The measured
  // total (`precip_in`, field radar first) stays the headline; % of normal and the
  // departure are gridMET over gridMET's own normal, on the days gridMET reported.
  const pairDays = obs.filter((d) => d.precip_gm_in != null && d.normal_precip_in != null);
  const normal = sum(pairDays.map((d) => d.normal_precip_in));
  const gm = sum(pairDays.map((d) => d.precip_gm_in));
  const normalDays = pairDays.length;

  // Freeze EVENTS, not just a count of days — consecutive days are one event, which is
  // what a freeze claim is actually about.
  const events = [];
  let cur = null;
  for (const d of obs) {
    if (d.freeze === true) {
      if (!cur) cur = { from: d.date, to: d.date, days: 1, min_f: d.tmin_f };
      else { cur.to = d.date; cur.days++; if (d.tmin_f != null && (cur.min_f == null || d.tmin_f < cur.min_f)) cur.min_f = d.tmin_f; }
    } else if (cur) { events.push(cur); cur = null; }
  }
  if (cur) events.push(cur);

  // The longest run with no measurable rain — the drought statistic an adjuster asks for.
  let best = null, runStart = null, runDays = 0;
  for (const d of obs) {
    const dry = d.precip_in == null ? false : d.precip_in < wetDayIn;
    if (dry) { if (!runStart) runStart = d.date; runDays++; if (!best || runDays > best.days) best = { from: runStart, to: d.date, days: runDays }; }
    else { runStart = null; runDays = 0; }
  }

  const years = num(obs.map((d) => d.normal_years));
  return {
    days_observed: obs.length,
    days_forecast: fc.length,
    first: obs.length ? obs[0].date : null,
    last: obs.length ? obs[obs.length - 1].date : null,

    precip_in: r2(precip),
    precip_gm_in: normalDays ? r2(gm) : null,
    normal_precip_in: normalDays ? r2(normal) : null,
    precip_pct_normal: normalDays && normal > 0 ? Math.round((gm / normal) * 1000) / 10 : null,
    precip_departure_in: normalDays ? r2(gm - normal) : null,
    // ⚠ NAMED, NOT SILENT: a departure computed over fewer days than the window is a
    // different statistic, and the gap is where a wrong conclusion gets in.
    normal_covers_days: normalDays,
    normal_years_min: years.length ? Math.min(...years) : null,

    wet_days: obs.filter((d) => d.precip_in != null && d.precip_in >= wetDayIn).length,
    max_day_in: obs.length ? r2(Math.max(0, ...num(obs.map((d) => d.precip_in)))) : null,
    longest_dry_run: best && best.days >= dryRunMin ? best : null,

    gdd: r1(sum(obs.map((d) => d.gdd))),
    freeze_days: obs.filter((d) => d.freeze === true).length,
    freeze_events: events,
    coldest_f: obs.length && num(obs.map((d) => d.tmin_f)).length ? r1(Math.min(...num(obs.map((d) => d.tmin_f)))) : null,
    hottest_f: obs.length && num(obs.map((d) => d.tmax_f)).length ? r1(Math.max(...num(obs.map((d) => d.tmax_f)))) : null,
    freeze_threshold_f: freezeF,

    forecast_precip_in: fc.length ? r2(sum(fc.map((d) => d.precip_in))) : null,

    // Which instruments actually produced this window, counted. A sheet that says
    // "MRMS" while two thirds of its days came from gridMET is overstating itself.
    sources: obs.reduce((m, d) => { const k = d.precip_source || "unknown"; m[k] = (m[k] || 0) + 1; return m; }, {}),
  };
}

// ── ABOVE, BELOW OR NORMAL — PER METRIC ───────────────────────────────────────
//
// Kevin, 2026-10-13: *"Can we somehow quickly show whether the selected range is above
// or below or normal for each piece of data?"*
//
// ⚠ THE NEAR-NORMAL BAND IS A READING AID, NOT A STATISTICAL TEST, AND SAYS SO.
// `wny_grid_climatology` stores a MEAN and a `year_count` and no spread at all — no
// standard deviation, no percentiles — so there is nothing here from which to compute
// whether a departure is unusual. Calling 112% of normal "significant" would be
// inventing the one number the table does not carry. What IS a fact is the departure
// and the ratio, so those are printed; the band only decides which of three words sits
// beside them, and the band itself is printed next to it.
const NEAR_BAND_RATIO = 0.05;   // accumulations and means of a rate: within 5% of normal
const NEAR_BAND_F = 1.0;        // temperature means: within 1.0 °F

// The settings the stored normals were computed at — hardcoded in
// tools/grid/build-weather.js, NOT configurable, and therefore the only settings at
// which a comparison against them is the same quantity on both sides.
const NORMAL_GDD_BASE_F = 50;
const NORMAL_FREEZE_F = 28;
const NORMAL_HEAT_F = 90;

function compareToNormal(series = [], { gddBase = 50, freezeF = 28 } = {}) {
  const gddOk = Number(gddBase) === NORMAL_GDD_BASE_F;
  const frzOk = Number(freezeF) === NORMAL_FREEZE_F;

  const METRICS = [
    { key: "precip", label: "Rainfall", unit: "in", kind: "sum", band: "ratio",
      pick: (d) => d.precip_gm_in, norm: (d) => d.normal_precip_in },
    { key: "tmax", label: "Average high", unit: "°F", kind: "mean", band: "degF",
      pick: (d) => d.tmax_f, norm: (d) => d.normal_tmax_f },
    { key: "tmin", label: "Average low", unit: "°F", kind: "mean", band: "degF",
      pick: (d) => d.tmin_f, norm: (d) => d.normal_tmin_f },
    { key: "gdd", label: `Growing degree days (base ${gddBase}°F)`, unit: "GDD", kind: "sum", band: "ratio",
      pick: (d) => d.gdd, norm: (d) => d.normal_gdd50,
      blocked: gddOk ? null
        : `the stored normal is published at base ${NORMAL_GDD_BASE_F}°F; this window is quoted at base ${gddBase}°F, which is a different quantity`,
    },
    { key: "freeze_days", label: `Days at or below ${freezeF}°F`, unit: "days", kind: "count", band: "ratio",
      pick: (d) => d.freeze === true, norm: (d) => d.normal_freeze28_prob,
      blocked: frzOk ? null
        : `the stored normal counts days at or below ${NORMAL_FREEZE_F}°F; this window is set to ${freezeF}°F`,
    },
    { key: "heat_days", label: `Days above ${NORMAL_HEAT_F}°F`, unit: "days", kind: "count", band: "ratio",
      pick: (d) => d.heat90 === true, norm: (d) => d.normal_heat90_prob },
    { key: "srad", label: "Average solar", unit: "W/m²", kind: "mean", band: "ratio",
      pick: (d) => d.srad_wm2, norm: (d) => d.normal_srad_wm2 },
    { key: "vpd", label: "Average VPD", unit: "kPa", kind: "mean", band: "ratio",
      pick: (d) => d.vpd_kpa, norm: (d) => d.normal_vpd_kpa },
  ];

  const out = [];
  for (const m of METRICS) {
    // ⚠ BOTH HALVES SUM OVER THE SAME DAYS. A normal accumulated over days the
    // instrument never reported covers a longer period than the measurement it is
    // being compared with, and the window reads drier or cooler than it was with
    // nothing on screen to say so. A day counts only if BOTH sides have a value, and
    // never if it is a forecast.
    let n = 0, v = 0, nv = 0;
    for (const d of series) {
      if (d.is_forecast) continue;
      const a = m.kind === "count" ? (m.pick(d) === true ? 1 : m.pick(d) === false ? 0 : null) : num(m.pick(d));
      const b = num(m.norm(d));
      if (a == null || b == null) continue;
      n++; v += a; nv += b;
    }

    const row = {
      key: m.key, label: m.label, unit: m.unit, kind: m.kind,
      days_compared: n,
      value: null, normal: null, departure: null, pct_of_normal: null,
      state: null, comparable: !m.blocked, reason: m.blocked || null,
      band: m.band === "degF" ? `±${NEAR_BAND_F}°F` : `±${Math.round(NEAR_BAND_RATIO * 100)}%`,
    };

    if (n === 0) {
      row.reason = row.reason || "no day in this window has both a reading and a published normal";
      row.comparable = false;
      out.push(row);
      continue;
    }

    const value = m.kind === "mean" ? v / n : v;
    const normal = m.kind === "mean" ? nv / n : nv;
    row.value = m.unit === "°F" || m.unit === "W/m²" ? r1(value) : m.unit === "kPa" ? r2(value) : r2(value);
    row.normal = m.unit === "°F" || m.unit === "W/m²" ? r1(normal) : m.unit === "kPa" ? r2(normal) : r2(normal);

    // A blocked metric still reports its own VALUE — the reading is real, it is only
    // the comparison that cannot be made. Blanking it would hide a measurement.
    if (m.blocked) { row.normal = null; out.push(row); continue; }

    row.departure = m.unit === "°F" || m.unit === "W/m²" ? r1(value - normal) : r2(value - normal);
    row.pct_of_normal = normal > 0 ? Math.round((value / normal) * 1000) / 10 : null;

    if (m.band === "degF") {
      row.state = Math.abs(value - normal) <= NEAR_BAND_F ? "near" : value > normal ? "above" : "below";
    } else if (m.kind === "count" && normal < 1) {
      // ⚠ YOU CANNOT BE MEANINGFULLY BELOW AN EXPECTATION OF A FIFTH OF A DAY.
      // A count is floored at zero and, when fewer than one event is expected across
      // the whole window, zero IS the modal outcome — so "0 freeze days, normal 0.2,
      // BELOW normal" reads as a finding about a September that did exactly what
      // September does. Measured on the first run of this rule, which is what caught
      // it. Below is genuinely unavailable here, so the only two honest readings are
      // "none, and about none expected" and "it happened, which is more than typical".
      row.state = value === 0 ? "near" : "above";
      if (value === 0) row.note = `fewer than one expected across this window (${r2(normal)}), so "below" is not a reading this can give`;
    } else if (normal === 0) {
      // Expected zero — "near" when it did not happen, "above" the moment it did.
      // A ratio is undefined here, which is why the state is decided on the value.
      row.state = value === 0 ? "near" : "above";
    } else {
      const rel = value / normal - 1;
      row.state = Math.abs(rel) <= NEAR_BAND_RATIO ? "near" : rel > 0 ? "above" : "below";
    }
    out.push(row);
  }
  return out;
}

// ── SORT AND FILTER, AS THE PAGE USES THEM ─────────────────────────────────────
// Kept here rather than in the view so the printed sheet and the screen cannot show
// different rows for the same question.
const FILTERS = {
  all: { label: "All days", test: () => true },
  wet: { label: "Rain ≥ 0.10\"", test: (d) => d.precip_in != null && d.precip_in >= 0.1 },
  heavy: { label: "Rain ≥ 1.00\"", test: (d) => d.precip_in != null && d.precip_in >= 1 },
  dry: { label: "No measurable rain", test: (d) => d.precip_in != null && d.precip_in < 0.01 },
  freeze: { label: "At or below freezing threshold", test: (d) => d.freeze === true },
  hot: { label: "Max ≥ 86°F", test: (d) => d.tmax_f != null && d.tmax_f >= 86 },
  above: { label: "Wetter than normal", test: (d) => d.precip_gm_in != null && d.normal_precip_in != null && d.precip_gm_in > d.normal_precip_in },
  below: { label: "Drier than normal", test: (d) => d.precip_gm_in != null && d.normal_precip_in != null && d.precip_gm_in < d.normal_precip_in },
};

function applyView(series, { filter = "all", sort = "date", dir = "asc" } = {}) {
  const f = FILTERS[filter] || FILTERS.all;
  const rows = series.filter(f.test);
  const key = sort;
  // ⚠ NULLS LAST IN BOTH DIRECTIONS, which a trailing .reverse() cannot do — it moved
  // every missing reading to the TOP of a descending sort. A day the instrument did
  // not report is not the wettest day, and on "driest days" it is not the driest
  // either. The direction is applied inside the comparator so the null rule survives it.
  const sign = dir === "desc" ? -1 : 1;
  return rows.slice().sort((a, b) => {
    const av = a[key], bv = b[key];
    if (av == null && bv == null) return a.date < b.date ? -1 : 1;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av === bv) return a.date < b.date ? -1 : 1;   // stable by date
    return (av < bv ? -1 : 1) * sign;
  });
}

module.exports = {
  mmToIn, cToF, haversineKm, describeSnap, dayOfYear, gridmetPrecipMm, radarPrecipMm,
  buildSeries, summarize, applyView, compareToNormal,
  FILTERS, SOURCE_LABEL,
  LATTICE_DEG, LATTICE_KM_NS, LATTICE_KM_EW, SNAP_NEAR_KM, SNAP_FAR_KM,
  NEAR_BAND_RATIO, NEAR_BAND_F, NORMAL_GDD_BASE_F, NORMAL_FREEZE_F, NORMAL_HEAT_F,
};
